import {isFeatureEnabled} from '@github-ui/feature-flags'
import {type ReactPartialAnchorProps, useExternalAnchor} from '@github-ui/react-core/react-partial-anchor'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {
  CodespacesIcon,
  CommentDiscussionIcon,
  CopilotIcon,
  GiftIcon,
  GitPullRequestIcon,
  GlobeIcon,
  HomeIcon,
  IssueOpenedIcon,
  McpIcon,
  RepoIcon,
  SparkleIcon,
  TableIcon,
  TelescopeIcon,
  ThreeBarsIcon,
} from '@primer/octicons-react'
import {type DialogProps, IconButton, NavList} from '@primer/react'
import {useCallback, useRef, useState} from 'react'

import navItemStyles from '../primitives/styles.module.css'
import {GlobalNavAnalyticsProvider} from '../utils/Analytics'
import {DynamicSidePanelContent} from './dynamic-content/DynamicSidePanelContent'
import {SideNavMenu, SideNavMenuItem, type SideNavMenuItemProps} from './SideNavMenu'

type ConfigKeys = (typeof GlobalMenuItems)[number][number]['key']

const defaultCategory = 'GLOBAL_NAV_MENU'

export type WithShow<T> = T & {
  /** Enables overriding the default to hide/show the menu-item on selective configurations */
  show?: boolean
}

type NavMenuOverrides = Partial<Record<ConfigKeys, Partial<WithShow<Pick<GlobalNavMenuItem, 'href' | 'hotkey'>>>>>

export type GlobalNavMenuProps = NavMenuOverrides & {
  /**
   * If the menu is currently open or not
   * Even when the menu is closed, this is required to support user hot-keys for menu items behind the drawer.
   * @default true
   */
  open?: boolean

  richContent?: {
    /**
     * Enables the display of rich content in the navigation menu.
     * This is used to display dynamic content such as repositories and teams.
     *
     * @default false
     */
    show?: boolean

    /**
     * The location to pull the initial config for rich content from.
     * These endpoints should return the initial data required to populate teams & repositories.
     * If `richContentEnabled` is false or not provided, these URLs will be ignored.
     */
    contentUrl?: string

    repositoriesSearchUrl?: string
    teamsSearchUrl?: string
  }
}

/**
 * The main navigation menu component for the application.
 * It renders a side navigation menu with support for rich content.
 *
 * Handling for specific menu-items can be controlled via the individual props:
 * i.e.
 * ```tsx
 * <GlobalNavMenu
 *   home={{ show: true, href: '/custom-home', hotkey: 'h' }}
 *   issues={{ show: false }}
 * />
 * ```
 *
 */
export function GlobalNavMenu(props: GlobalNavMenuProps & Pick<DialogProps, 'onClose' | 'returnFocusRef'>) {
  const {open = true, onClose, richContent} = props

  const [mainGroup, secondaryGroup] = GlobalMenuItems.map(group => {
    return group
      .filter(item => {
        if (props[item.key]?.show !== undefined) {
          return props[item.key]?.show
        }

        return item.show !== false
      })
      .map(({show: _show, ...item}) => {
        const {show, ...overrides} = props[item.key] ?? {}

        // Conditionally set copilot href and analytics key based on feature flags
        const copilotExperimentOverride = {}
        if (item.key === 'copilot') {
          let analyticsKey = item.analyticsKey
          if (isFeatureEnabled('copilot_default_to_task_input')) {
            analyticsKey += '_TASK'
            Object.assign(copilotExperimentOverride, {
              href: '/copilot/agents',
            })
          }
          if (isFeatureEnabled('copilot_default_to_task_input_control_group')) {
            analyticsKey += '_TASK_EXPERIMENT_CONTROL'
          }
          Object.assign(copilotExperimentOverride, {
            analyticsKey,
          })
        }

        return {
          ...item,
          ...overrides,
          ...copilotExperimentOverride,
        }
      })
  })

  if (!open) {
    // If the menu is not open, we don't render anything visually.
    // However, we still render the nav-items that have hot-keys assigned to them, otherwise nothing will listen for the events
    return (
      <GlobalNavAnalyticsProvider category={defaultCategory}>
        <GlobalNavHotKeys menuItems={[...(mainGroup ?? []), ...(secondaryGroup ?? [])]} />
      </GlobalNavAnalyticsProvider>
    )
  }

  return (
    <GlobalNavAnalyticsProvider category={defaultCategory}>
      <SideNavMenu onClose={onClose}>
        <NavList.Group>
          {mainGroup?.map(({key, ...item}) => {
            return <SideNavMenuItem key={key} {...item} />
          })}
        </NavList.Group>

        {(secondaryGroup?.length ?? 0) > 0 && (
          <NavList.Group>
            {secondaryGroup?.map(({key, ...item}) => {
              return <SideNavMenuItem key={key} {...item} />
            })}
          </NavList.Group>
        )}
        {richContent?.show && richContent.contentUrl && (
          <DynamicSidePanelContent
            richContentUrl={richContent.contentUrl}
            repositoriesSearchUrl={richContent.repositoriesSearchUrl}
            teamsSearchUrl={richContent.teamsSearchUrl}
          />
        )}
      </SideNavMenu>
    </GlobalNavAnalyticsProvider>
  )
}

/**
 * Renders a hidden version of the GlobalNavMenu to support user hot-keys for any menu-item that has it enabled.
 * This component does not render anything visual, and should be used when the menu is collapsed
 */
export function GlobalNavHotKeys(props: {menuItems: GlobalNavMenuItem[]}) {
  return (
    <div className="d-none">
      {props.menuItems.map(({key, ...item}) => {
        if (!item.hotkey) {
          return null
        }

        return <SideNavMenuItem key={key} {...item} />
      })}
    </div>
  )
}

export function ExternallyAnchoredGlobalNavMenu(props: GlobalNavMenuProps & Required<ReactPartialAnchorProps>) {
  const {ref: anchorRef, open, setOpen} = useExternalAnchor(props.reactPartialAnchor)

  const onClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  return <GlobalNavMenu returnFocusRef={anchorRef} open={open} onClose={onClose} {...props} />
}

export interface InternallyAnchoredGlobalNavMenuProps extends GlobalNavMenuProps {
  /**
   * The initial state of the menu on the first render before being controlled
   */
  initialMenuOpen?: boolean
}

export function InternallyAnchoredGlobalNavMenu(props: InternallyAnchoredGlobalNavMenuProps) {
  const [open, setOpen] = useState(props.initialMenuOpen ?? false)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  const onClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  return (
    <>
      <IconButton
        variant="invisible"
        className={navItemStyles.appHeaderButton}
        aria-haspopup="dialog"
        onClick={() => {
          setOpen(!open)
          sendClickAnalyticsEvent({category: defaultCategory, action: 'GLOBAL_NAV_MENU_TOGGLE'})
        }}
        aria-label="Open Menu"
        icon={ThreeBarsIcon}
      />
      {<GlobalNavMenu returnFocusRef={anchorRef} open={open} onClose={onClose} {...props} />}
    </>
  )
}

export function GlobalNavMenuPartial(props: GlobalNavMenuProps & ReactPartialAnchorProps) {
  if (props.reactPartialAnchor) {
    return <ExternallyAnchoredGlobalNavMenu reactPartialAnchor={props.reactPartialAnchor} {...props} />
  }

  return <InternallyAnchoredGlobalNavMenu {...props} />
}

export type GlobalNavMenuItem = SideNavMenuItemProps & {
  /** The config key to use when overriding props */
  key: string
}

export const GlobalMenuItems = [
  [
    {
      href: '/dashboard',
      key: 'home',
      icon: HomeIcon,
      label: 'Home',
      analyticsKey: 'HOME',
      show: true,
      activePath: {
        additionalPathnames: ['/'],
      },
    },
    {
      href: '/feed',
      key: 'feed',
      icon: GlobeIcon,
      label: 'Feed',
      analyticsKey: 'FEED',
      show: false,
    },
    {
      href: '/issues',
      key: 'issues',
      icon: IssueOpenedIcon,
      label: 'Issues',
      analyticsKey: 'ISSUES',
      show: true,
      activePath: {
        includeSubpaths: true,
      },
    },
    {
      href: '/pulls',
      key: 'pulls',
      icon: GitPullRequestIcon,
      label: 'Pull requests',
      analyticsKey: 'PULL_REQUESTS',
      show: true,
      activePath: {
        includeSubpaths: true,
      },
    },
    {
      href: '/repos',
      key: 'contributedRepos',
      label: 'Repositories',
      analyticsKey: 'CONTRIBUTED_REPOS',
      icon: RepoIcon,
      show: false,
    },
    {
      href: '/projects',
      key: 'projects',
      icon: TableIcon,
      label: 'Projects',
      analyticsKey: 'PROJECTS',
      show: true,
    },
    {
      href: '/discussions',
      key: 'discussions',
      icon: CommentDiscussionIcon,
      label: 'Discussions',
      analyticsKey: 'DISCUSSIONS',
      show: false,
      activePath: {
        includeSubpaths: true,
      },
    },
    {
      href: '/codespaces',
      key: 'codespaces',
      icon: CodespacesIcon,
      label: 'Codespaces',
      analyticsKey: 'CODESPACES',
      show: false,
      activePath: {
        includeSubpaths: true,
      },
    },
    {
      href: '/copilot',
      key: 'copilot',
      icon: CopilotIcon,
      label: 'Copilot',
      analyticsKey: 'COPILOT',
      show: false,
    },
    {
      href: '/spark',
      key: 'spark',
      icon: SparkleIcon,
      label: 'Spark',
      analyticsKey: 'SPARK',
      show: false,
    },
  ],
  [
    {
      href: '/explore',
      key: 'explore',
      icon: TelescopeIcon,
      label: 'Explore',
      analyticsKey: 'EXPLORE',
      show: false,
    },
    {
      href: '/marketplace',
      key: 'marketplace',
      icon: GiftIcon,
      label: 'Marketplace',
      analyticsKey: 'MARKETPLACE',
      show: false,
    },
    {
      href: '/mcp',
      key: 'mcp',
      icon: McpIcon,
      label: 'MCP registry',
      analyticsKey: 'MCP_REGISTRY',
      show: false,
      activePath: {
        includeSubpaths: true,
      },
    },
  ],
  // The `as const` lets us pull out the `key` properties in TS dynamically
  // The `satisfies` lets us still type-check this object against the GlobalNavMenuItem structure
] as const satisfies Array<Array<WithShow<GlobalNavMenuItem>>>

try{ GlobalNavMenu.displayName ||= 'GlobalNavMenu' } catch {}
try{ GlobalNavHotKeys.displayName ||= 'GlobalNavHotKeys' } catch {}
try{ ExternallyAnchoredGlobalNavMenu.displayName ||= 'ExternallyAnchoredGlobalNavMenu' } catch {}
try{ InternallyAnchoredGlobalNavMenu.displayName ||= 'InternallyAnchoredGlobalNavMenu' } catch {}
try{ GlobalNavMenuPartial.displayName ||= 'GlobalNavMenuPartial' } catch {}